import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { AvatarModule } from 'primeng/avatar';
import { MenuItem } from 'primeng/api';
import { ProjectTask } from '../../../models/task-management.models';
import { TimezoneService } from '../../../services/timezone.service';

@Component({
  selector: 'app-task-card',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    BadgeModule,
    TooltipModule,
    MenuModule,
    AvatarModule
  ],
  template: `
    <div class="task-card" 
         [ngClass]="{'task-overdue': task.isOverdue, 'task-due-today': task.isDueToday}"
         (click)="onTaskClick()">
      
      <!-- Task Header -->
      <div class="task-header">
        <div class="task-title-section">
          <h4 class="task-title" [pTooltip]="task.title">{{ task.title }}</h4>
          <div class="task-id">#{{ task.taskId }}</div>
        </div>
        
        <div class="task-actions">
          <p-button 
            icon="pi pi-ellipsis-v" 
            [text]="true" 
            size="small"
            (click)="onMenuClick($event)" />
          
          <p-menu #menu [model]="menuItems" [popup]="true" />
        </div>
      </div>

      <!-- Task Description -->
      <div class="task-description" *ngIf="task.description && showDescription">
        <p [pTooltip]="task.description">{{ truncateText(task.description, 100) }}</p>
      </div>

      <!-- Task Status -->
      <div class="task-status" *ngIf="showStatus">
        <p-tag 
          [value]="task.statusName" 
          [style]="{'background-color': task.statusColor, 'color': getContrastColor(task.statusColor || '')}"
          severity="info" />
      </div>

      <!-- Task Tags -->
      <div class="task-tags" *ngIf="task.tags && task.tags.length > 0 && showTags">
        <p-tag 
          *ngFor="let tag of task.tags.slice(0, 3)" 
          [value]="tag.tagName"
          severity="secondary"
          size="small" />
        <span *ngIf="task.tags.length > 3" class="more-tags">+{{ task.tags.length - 3 }} more</span>
      </div>

      <!-- Task Metadata -->
      <div class="task-metadata">
        <div class="task-assignee" *ngIf="task.assignedToName">
          <p-avatar 
            [label]="getInitials(task.assignedToName)"
            size="normal"
            shape="circle"
            [pTooltip]="'Assigned to: ' + task.assignedToName" />
        </div>

        <div class="task-dates">
          <div class="task-due-date" *ngIf="task.endDate">
            <i class="pi pi-calendar" [ngClass]="{
              'text-danger': task.isOverdue,
              'text-warning': task.isDueToday,
              'text-muted': !task.isOverdue && !task.isDueToday
            }"></i>
            <span [ngClass]="{
              'text-danger': task.isOverdue,
              'text-warning': task.isDueToday,
              'text-muted': !task.isOverdue && !task.isDueToday
            }">{{ formatDate(task.endDate) }}</span>
          </div>
        </div>

        <div class="task-stats" *ngIf="task.hasSubTasks">
          <p-badge 
            [value]="task.completedSubTasksCount + '/' + task.subTasksCount" 
            severity="info" 
            [pTooltip]="'Subtasks completed'" />
        </div>

        <div class="task-comments" *ngIf="task.comments && task.comments.length > 0">
          <i class="pi pi-comment"></i>
          <span>{{ task.comments.length }}</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .task-card {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 12px;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-bottom: 8px;
    }

    .task-card:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      border-color: #007ad9;
    }

    .task-card.task-overdue {
      border-left: 4px solid #e74c3c;
    }

    .task-card.task-due-today {
      border-left: 4px solid #f39c12;
    }

    .task-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;
    }

    .task-title-section {
      flex: 1;
    }

    .task-title {
      font-size: 14px;
      font-weight: 600;
      margin: 0 0 4px 0;
      line-height: 1.2;
      color: #2c3e50;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 200px;
    }

    .task-id {
      font-size: 12px;
      color: #7f8c8d;
      font-weight: 500;
    }

    .task-actions {
      position: relative;
    }

    .task-description {
      margin-bottom: 8px;
    }

    .task-description p {
      font-size: 12px;
      color: #7f8c8d;
      margin: 0;
      line-height: 1.3;
    }

    .task-status {
      margin-bottom: 8px;
    }

    .task-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-bottom: 8px;
    }

    .more-tags {
      font-size: 12px;
      color: #7f8c8d;
      font-style: italic;
    }

    .task-metadata {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-top: 12px;
    }

    .task-assignee {
      flex-shrink: 0;
    }

    .task-dates {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .task-due-date {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
    }

    .task-stats {
      flex-shrink: 0;
    }

    .task-comments {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #7f8c8d;
    }

    .text-danger {
      color: #e74c3c !important;
    }

    .text-warning {
      color: #f39c12 !important;
    }

    .text-muted {
      color: #7f8c8d !important;
    }

    @media (max-width: 768px) {
      .task-card {
        padding: 10px;
      }
      
      .task-title {
        max-width: 150px;
        font-size: 13px;
      }
      
      .task-metadata {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
      }
    }
  `]
})
export class TaskCardComponent implements OnInit {
  @Input() task!: ProjectTask;
  @Input() showDescription: boolean = true;
  @Input() showStatus: boolean = false; // Don't show status in Kanban columns
  @Input() showTags: boolean = true;
  @Input() draggable: boolean = false;

  @Output() taskClick = new EventEmitter<ProjectTask>();
  @Output() taskEdit = new EventEmitter<ProjectTask>();
  @Output() taskDelete = new EventEmitter<ProjectTask>();
  @Output() statusChange = new EventEmitter<{task: ProjectTask, statusId: number}>();

  menuItems: MenuItem[] = [];

  constructor(private timezoneService: TimezoneService) {}

  ngOnInit() {
    this.initializeMenu();
  }

  private initializeMenu() {
    this.menuItems = [
      {
        label: 'View Details',
        icon: 'pi pi-eye',
        command: () => this.onTaskClick()
      },
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.taskEdit.emit(this.task)
      },
      {
        separator: true
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        styleClass: 'text-danger',
        command: () => this.taskDelete.emit(this.task)
      }
    ];
  }

  onTaskClick() {
    this.taskClick.emit(this.task);
  }

  onMenuClick(event: Event) {
    event.stopPropagation();
  }

  truncateText(text: string, length: number): string {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  }

  getInitials(name: string): string {
    if (!name) return '??';
    return name.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    return this.timezoneService.formatStoredDate(date) || '';
  }

  getContrastColor(hexColor: string): string {
    if (!hexColor) return '#000000';
    
    // Remove # if present
    hexColor = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black or white based on luminance
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }
}
